import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Users, GraduationCap } from 'lucide-react';
import axios from 'axios';

const API = '/api';

export default function AdultAuthChoice() {
  const navigate = useNavigate();
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    // Check if user is already authenticated
    const checkAuth = async () => {
      try {
        const response = await axios.get(`${API}/auth/me`);
        
        const user = response.data;
        // User is already logged in, redirect based on role
        if (user.role === 'teacher') {
          navigate('/teacher/home', { replace: true });
        } else {
          navigate('/dashboard', { replace: true });
        }
      } catch (error) {
        // Not authenticated, show choice
        setChecking(false);
      }
    };
    
    checkAuth();
  }, [navigate]);

  const handleParentLogin = () => {
    const redirectUrl = window.location.origin + '/auth/callback/parent';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleTeacherLogin = () => {
    // School teachers now use the school-branded /school/:slug/login flow
    navigate('/');
  };

  if (checking) {
    return (
      <div className="adult-layout min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Checking authentication...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="adult-layout min-h-screen flex items-center justify-center">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-2xl mx-auto px-4 w-full"
      >
        <div className="text-center mb-12">
          <h1 className="adult-heading text-4xl font-bold text-gray-900 mb-4">
            Welcome Back!
          </h1>
          <p className="text-gray-600 text-lg">How would you like to sign in?</p>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          {/* Parent Option */}
          <motion.div
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.97 }}
            onClick={handleParentLogin}
            data-testid="parent-login-button"
            className="warm-card rounded-3xl p-8 cursor-pointer text-center shadow-lg hover:shadow-xl transition-all"
          >
            <div className="w-20 h-20 mx-auto mb-4 bg-gradient-to-br from-blue-400 to-blue-600 rounded-full flex items-center justify-center shadow-md">
              <Users className="w-10 h-10 text-white" />
            </div>
            <h2 className="adult-heading text-2xl font-bold text-gray-900 mb-2">Parent</h2>
            <p className="text-gray-600 text-sm">Manage my children's learning</p>
          </motion.div>

          {/* Teacher Option */}
          <motion.div
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.97 }}
            onClick={handleTeacherLogin}
            data-testid="teacher-login-button"
            className="warm-card rounded-3xl p-8 cursor-pointer text-center shadow-lg hover:shadow-xl transition-all"
          >
            <div className="w-20 h-20 mx-auto mb-4 bg-gradient-to-br from-purple-400 to-purple-600 rounded-full flex items-center justify-center shadow-md">
              <GraduationCap className="w-10 h-10 text-white" />
            </div>
            <h2 className="adult-heading text-2xl font-bold text-gray-900 mb-2">Teacher</h2>
            <p className="text-gray-600 text-sm">Manage my classroom</p>
          </motion.div>
        </div>
      </motion.div>
    </div>
  );
}
